import { agents } from './agents.js';
import { escapeHtml, formatTimeAgo } from './utils.js';

export function renderLoadingState(container) {
  container.innerHTML = `
    <div class="loading-state" role="status" aria-live="polite">
      <div class="loading-spinner" aria-hidden="true"></div>
      <h3>// SYNCING FEED</h3>
      <p>Pulling latest AI stories from the wire...</p>
    </div>
  `;
}

export function renderAudioBriefingCard(container, episode) {
  if (!container) return;
  if (!episode) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = `
    <section class="audio-briefing-card">
      <div class="audio-briefing-header">🎧 TODAY'S AI BRIEFING</div>
      <div class="audio-briefing-body">
        <div class="audio-briefing-meta">
          <span>${formatDuration(episode.durationSeconds)}</span>
          <span>${episode.storyCount} STORIES</span>
          <span>PREVIEW</span>
        </div>
        <h2 class="audio-briefing-title">${escapeHtml(episode.title)}</h2>
        <p class="audio-briefing-summary">${escapeHtml(episode.summary)}</p>
        <audio class="audio-player" controls preload="none" src="${escapeHtml(episode.previewUrl)}"></audio>
        <div class="audio-briefing-links">
          <a class="audio-link-btn" href="${escapeHtml(episode.archiveUrl)}">LISTEN ON ARCHIVE</a>
          <a class="audio-link-btn secondary" href="${escapeHtml(baseArchiveUrl(episode.archiveUrl))}">ALL EPISODES</a>
        </div>
      </div>
    </section>
  `;
}

export function renderAudioArchive(container, episodes, selectedEpisodeId = null) {
  if (!container) return;
  if (!episodes.length) {
    container.innerHTML = `
      <div class="empty-state">
        <h3>// NO_BRIEFINGS_YET</h3>
        <p>The daily audio archive will appear here once the first episode is generated.</p>
      </div>
    `;
    return;
  }

  const selected = episodes.find((episode) => episode.id === selectedEpisodeId) || episodes[0];
  const remaining = episodes.filter((episode) => episode.id !== selected.id);

  container.innerHTML = `
    <section class="audio-archive">
      <div class="audio-archive-intro">
        <h2>// DAILY AI BRIEFINGS</h2>
        <p>The Neural Feed archive is the main listening surface: play the full episode here, then browse recent briefings below.</p>
      </div>
      ${renderFeaturedAudioEpisode(selected)}
      <div class="audio-archive-list">
        <h3 class="audio-archive-list-heading">// MORE EPISODES</h3>
        ${remaining.map(renderAudioArchiveItem).join('')}
      </div>
    </section>
  `;
}

export function renderFeed(container, posts) {
  if (posts.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <h3>// NO POSTS_DETECTED</h3>
        <p>The neural network is silent. Start the chaos.</p>
      </div>
    `;
    return;
  }

  container.innerHTML = posts.map(post => renderPost(post)).join('');
}

export function renderPost(post) {
  if (post.postType === 'debate' || post.postType === 'story_prompt') {
    return renderDebateCard(post);
  }

  const agent = agents[post.agentId] || agents.unknown;
  const timeAgo = formatTimeAgo(post.timestamp);
  const bgColor = agent.color + '20';

  return `
    <article class="post-card" id="post-${post.id}">
      <div class="post-header">
        <button class="agent-avatar avatar-button" style="background: ${bgColor};" onclick="openAgentProfile('${post.agentId}')">
          <img class="avatar-image" src="${agent.avatar}" alt="${escapeHtml(agent.name)} avatar" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
          <span class="avatar-fallback" style="display:none;">${agent.initials}</span>
        </button>
        <div class="agent-info">
          <div class="agent-name">${agent.name}</div>
          <div class="agent-tagline">${agent.tagline}</div>
        </div>
        <div class="timestamp">${timeAgo}</div>
      </div>
      <div class="post-content">
        <h2 class="post-headline">${escapeHtml(post.headline)}</h2>
        <div class="post-body">${post.content}</div>
        ${post.sourceUrl ? `
          <p class="source-link-row">
            <a class="source-link" href="${escapeHtml(post.sourceUrl)}" target="_blank" rel="noopener noreferrer">
              SOURCE ARTICLE ↗
            </a>
          </p>
        ` : ''}
      </div>
      <div class="post-actions">
        <button class="action-btn upvote-btn ${post.upvotedBy.includes('user') ? 'upvoted' : ''}"
                onclick="toggleUpvote('${post.id}', this)">
          <span class="action-icon">▲</span>
          <span class="upvote-count">${post.upvotes}</span> UPVOTE
        </button>
        <button class="action-btn" onclick="toggleComments('${post.id}')">
          <span class="action-icon">💬</span>
          ${post.comments.length} COMMENTS
        </button>
        <button class="action-btn" onclick="openAskStory('${post.id}')">
          <span class="action-icon">✦</span>
          ASK
        </button>
        <button class="action-btn" onclick="copyPostLink('${post.id}', this)">
          <span class="action-icon">🔗</span>
          LINK
        </button>
        <button class="action-btn" onclick="sharePost('${post.id}', this)">
          <span class="action-icon">↗</span>
          SHARE
        </button>
      </div>
      <div class="comments-section" id="comments-${post.id}">
        <div class="comments-header">
          // AGENT_DISCUSSIONS [${post.comments.length}]
        </div>
        <div class="comments-list" id="comments-list-${post.id}">
          ${post.comments.map(comment => renderComment(comment)).join('')}
        </div>
      </div>
    </article>
  `;
}

export function renderDebateCard(post) {
  const primaryAgent = agents[post.agentId] || agents.unknown;
  const timeAgo = formatTimeAgo(post.timestamp);
  const label = post.pending
    ? 'ASK IN PROGRESS'
    : (post.postType === 'story_prompt' ? 'ASKED THIS STORY' : 'SPARKED DEBATE');

  // Build agent avatars row (all 4 agents)
  const allAgentIds = ['serious_journalist', 'optimist_investor', 'doom_economist', 'meme_analyst'];
  const agentAvatarsHtml = allAgentIds.map(id => {
    const a = agents[id] || agents.unknown;
    const bgColor = a.color + '30';
    return `
      <button class="debate-agent-chip avatar-button" style="background: ${bgColor};" onclick="openAgentProfile('${id}')">
        <img class="avatar-image" src="${a.avatar}" alt="${escapeHtml(a.name)}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
        <span class="avatar-fallback" style="display:none;">${a.initials}</span>
      </button>
    `;
  }).join('');

  // Build debate takes (comments shown as equal panels, always visible)
  const debateTakesHtml = post.comments.map(comment => {
    const a = agents[comment.agentId] || agents.unknown;
    return `
      <div class="debate-take" style="border-left: 4px solid ${a.color};">
        <div class="debate-take-header">
          <button class="comment-avatar avatar-button" style="background: ${a.color}30;" onclick="openAgentProfile('${comment.agentId}')">
            <img class="avatar-image" src="${a.avatar}" alt="${escapeHtml(a.name)}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <span class="avatar-fallback" style="display:none;">${a.initials}</span>
          </button>
          <span class="debate-take-name">${a.name}</span>
        </div>
        <div class="debate-take-body">${renderCommentBody(comment.content)}</div>
      </div>
    `;
  }).join('');

  return `
    <article class="post-card debate-card ${post.pending ? 'debate-card-pending' : ''}" id="post-${post.id}">
      <div class="debate-banner">
        <span class="debate-label">${post.pending ? '⏳' : '⚡'} ${label}</span>
        <span class="timestamp">${timeAgo}</span>
      </div>
      ${post.topic ? `
        <div class="debate-topic">
          <span class="debate-topic-q">Q:</span> ${escapeHtml(post.topic)}
        </div>
      ` : ''}
      <div class="debate-agents-row">
        ${agentAvatarsHtml}
      </div>
      <div class="debate-primary-take">
        <div class="debate-take-header">
          <button class="comment-avatar avatar-button" style="background: ${primaryAgent.color}30;" onclick="openAgentProfile('${post.agentId}')">
            <img class="avatar-image" src="${primaryAgent.avatar}" alt="${escapeHtml(primaryAgent.name)}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <span class="avatar-fallback" style="display:none;">${primaryAgent.initials}</span>
          </button>
          <div class="agent-info">
            <div class="agent-name">${primaryAgent.name}</div>
            <div class="agent-tagline">${primaryAgent.tagline}</div>
          </div>
        </div>
        <h2 class="post-headline">${escapeHtml(post.headline)}</h2>
        <div class="post-body">${post.content}</div>
        ${post.sourceUrl ? `
          <p class="source-link-row">
            <a class="source-link" href="${escapeHtml(post.sourceUrl)}" target="_blank" rel="noopener noreferrer">
              SOURCE ARTICLE ↗
            </a>
          </p>
        ` : ''}
      </div>
      ${post.pending ? `
        <div class="debate-takes-section">
          <div class="debate-takes-header">// AGENTS_ARE_WORKING</div>
          <div class="debate-take debate-take-pending">
            <div class="debate-take-body">Pulling the story context together and drafting responses now.</div>
          </div>
        </div>
      ` : debateTakesHtml ? `
        <div class="debate-takes-section">
          <div class="debate-takes-header">// OTHER_AGENTS_RESPOND</div>
          ${debateTakesHtml}
        </div>
      ` : ''}
      <div class="post-actions">
        <button class="action-btn upvote-btn ${post.upvotedBy.includes('user') ? 'upvoted' : ''}"
                onclick="toggleUpvote('${post.id}', this)" ${post.pending ? 'disabled' : ''}>
          <span class="action-icon">▲</span>
          <span class="upvote-count">${post.upvotes}</span> UPVOTE
        </button>
        <button class="action-btn" onclick="copyPostLink('${post.id}', this)" ${post.pending ? 'disabled' : ''}>
          <span class="action-icon">🔗</span>
          LINK
        </button>
        <button class="action-btn" onclick="sharePost('${post.id}', this)" ${post.pending ? 'disabled' : ''}>
          <span class="action-icon">↗</span>
          SHARE
        </button>
      </div>
    </article>
  `;
}

export function renderComment(comment) {
  const agent = agents[comment.agentId] || agents.unknown;
  const bgColor = agent.color + '30';

  return `
    <div class="comment">
      <div class="comment-header">
        <button class="comment-avatar avatar-button" style="background: ${bgColor};" onclick="openAgentProfile('${comment.agentId}')">
          <img class="avatar-image" src="${agent.avatar}" alt="${escapeHtml(agent.name)} avatar" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
          <span class="avatar-fallback" style="display:none;">${agent.initials}</span>
        </button>
        <span class="comment-author">${agent.name}</span>
        <span class="comment-time">${formatTimeAgo(comment.timestamp)}</span>
      </div>
      <div class="comment-body">${renderCommentBody(comment.content)}</div>
    </div>
  `;
}

function renderCommentBody(content) {
  const raw = String(content || '');
  const mentionPattern = /@([a-z0-9_]+)/gi;
  let out = '';
  let idx = 0;
  let match = mentionPattern.exec(raw);
  while (match) {
    const start = match.index;
    const end = mentionPattern.lastIndex;
    out += escapeHtml(raw.slice(idx, start));
    const agentId = (match[1] || '').toLowerCase();
    if (agents[agentId]) {
      out += `<button class="mention-link" onclick="openAgentProfile('${agentId}')">@${agentId}</button>`;
    } else {
      out += escapeHtml(raw.slice(start, end));
    }
    idx = end;
    match = mentionPattern.exec(raw);
  }
  out += escapeHtml(raw.slice(idx));
  return out;
}

export function renderAgentSelector(selectedAgent) {
  const selector = document.getElementById('agentSelector');
  if (!selector) return;

  selector.innerHTML = Object.entries(agents).filter(([id]) => id !== 'unknown').map(([id, agent]) => `
    <label class="agent-option ${selectedAgent === id ? 'selected' : ''}" 
           onclick="selectAgent('${id}')">
      <input type="radio" name="agent" value="${id}" ${selectedAgent === id ? 'checked' : ''}>
      <div class="agent-avatar" style="background: ${agent.color}33; width: 36px; height: 36px; font-size: 12px;">
        <img class="avatar-image" src="${agent.avatar}" alt="${escapeHtml(agent.name)} avatar" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
        <span class="avatar-fallback" style="display:none;">${agent.initials}</span>
      </div>
      <span>${agent.name}</span>
    </label>
  `).join('');
}

export function showToast(message) {
  const toast = document.getElementById('toast');
  if (!toast) return;
  
  toast.textContent = message;
  toast.classList.add('show');
  setTimeout(() => toast.classList.remove('show'), 3000);
}

function renderAudioArchiveItem(episode) {
  return `
    <article class="audio-archive-item" id="episode-${escapeHtml(episode.id)}">
      <div class="audio-archive-badges">
        <span>${escapeHtml(episode.date)}</span>
        <span>${formatDuration(episode.durationSeconds)}</span>
        <span>${episode.storyCount} STORIES</span>
      </div>
      <h3 class="audio-archive-title">${escapeHtml(episode.title)}</h3>
      <p class="audio-archive-summary">${escapeHtml(episode.summary)}</p>
      <div class="audio-briefing-links">
        <button class="audio-link-btn" type="button" onclick="selectAudioEpisode('${escapeHtml(episode.id)}')">PLAY THIS EPISODE</button>
      </div>
    </article>
  `;
}

function renderFeaturedAudioEpisode(episode) {
  return `
    <article class="audio-featured-episode" id="episode-${escapeHtml(episode.id)}">
      <div class="audio-briefing-header">▶ NOW PLAYING</div>
      <div class="audio-briefing-body">
        <div class="audio-briefing-meta">
          <span>${escapeHtml(episode.date)}</span>
          <span>${formatDuration(episode.durationSeconds)}</span>
          <span>${episode.storyCount} STORIES</span>
          <span>FULL EPISODE</span>
        </div>
        <h2 class="audio-briefing-title">${escapeHtml(episode.title)}</h2>
        <p class="audio-briefing-summary">${escapeHtml(episode.summary)}</p>
        <audio class="audio-player" controls preload="metadata" src="${escapeHtml(episode.audioUrl)}"></audio>
      </div>
    </article>
  `;
}

function baseArchiveUrl(url) {
  return String(url || '#/briefings').replace(/#\/briefings\/.+$/, '#/briefings');
}

function formatDuration(totalSeconds) {
  const seconds = Number(totalSeconds || 0);
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes} MIN`;
}
